import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Field, useField, useFormikContext } from 'formik';
import { Badge, Flex, Spinner } from '@chakra-ui/react';

import { isAutosaveAllowed } from 'helpers/rfxHelper';
import { useUnitsAndMeasures } from 'newui/store';
import { RfxQuoteHeader } from 'newui/features/Rfx/Submission/components/QuoteTab/RfxQuoteHeader';
import { showToast } from 'utils/helpers';
import { QuoteComment } from './QuoteComment';
import type { QuoteCommentHandle } from './QuoteComment';
import { QuoteSupportingDocuments } from './QuoteSupportingDocuments';
import { SupplierRowsSection } from './SupplierRowsSection';
import type { SupplierRowsSectionHandle } from './SupplierRowsSection';
import { DynamicQuoteTable } from '../../../components/QuoteTable';
import { formatCurrency, getRowDisplayName } from '../../../components/QuoteTable/utils';
import { buildReadOnlyRows, buildReadOnlyTotals, calculateReadOnlyColumnTotals, cellDataToLookup, computeRowTotalFromRowVals, mapCellSnapshotToRows, mapQuoteTableRowsToReadOnly } from './utils';
import {
    calculateTotal,
    computeRowSubtotal,
    draftCellsToFormikRow,
    getClientRows,
    getSupplierRows,
    handleKeyDown,
    handleNumberValue,
    mapRowToSupplierRow,
    TABLE_HEADERS,
    TOTAL_ROWS,
} from './BidderQuoteTab.helpers';
import type { QuoteFieldValues, TotalType } from './BidderQuoteTab.helpers';
import { useSupplierRowTotals } from './hooks/useSupplierRowTotals';
import { useDraftRevisions } from './hooks/useDraftRevisions';
import { useQuoteValidation } from './hooks/useQuoteValidation';
import { OverallTotalsSummary } from './components/OverallTotalsSummary';
import type { QuoteTableColumn, SupplierRow } from '../../../components/QuoteTable/quote-table.types';
import { ReadOnlyQuoteTable } from './components/ReadOnlyQuoteTable';
import type { QuoteSupportingDocument, RfxQuoteItem, RfxQuoteTable } from '../../types/quote.type';
import type { QuoteRevisionSnapshot } from '../../types/revision.type';
import { RFX_SUBMISSION_STATUS } from '../../constants';
import { GST_RATE } from '../../../config/gst.config';
import { cancelQuoteRevision, getQuoteRevisions, getSummaryTables } from '../../api';
import { CancelRevisionModal } from './components/CancelRevisionModal';
import { SummaryTable } from '../../../components/QuoteTable/SummaryTable';
import type { SummaryTableConfig } from '../../types/quote.type';
import { UpdateQuoteModal } from './UpdateQuoteModal';
import { CompareRevisionsModal } from './CompareRevisionsModal';
import { LoadingSpinner } from 'components/Loading/LoadingSpinner';
import { ScheduleOfRates, ScheduleOfRatesHandle } from 'newui/features/Rfx/components/ScheduleOfRates';
import { Button } from 'newui/components/Button';

/**
 * Module-level cache for supplier rows that survives tab-switch unmounts.
 * Keyed by quoteId. Cleared automatically on full page refresh (module reload).
 * This avoids the need to reload the entire submission data when supplier rows
 * are added or removed — the cache is the source of truth until the next API fetch.
 */
const supplierRowsCache = new Map<number, SupplierRow[]>();

/**
 * Module-level caches for the quote comment and supporting documents that
 * survive tab-switch unmounts, mirroring `supplierRowsCache` above.
 *
 * Comments and attachments are persisted via their own endpoints (not
 * `saveSubmission`), and `listQuotes` is a prop owned by a distant ancestor
 * (see F25 — deliberately not touched here). Without this cache, a
 * successful save has nowhere to live: the eager-loaded `quote.comments` /
 * `quote.attachments` snapshot stays stale until the next full submission
 * reload, so switching tabs away and back (which unmounts this whole
 * component) reseeds `QuoteComment` / `QuoteSupportingDocuments` from the
 * pre-edit value even though the save itself succeeded.
 * Cleared automatically on full page refresh (module reload).
 */
const quoteCommentsCache = new Map<number, string>();
const quoteAttachmentsCache = new Map<number, QuoteSupportingDocument[]>();

export interface QuoteTabHandle {
    validateAndSave: () => Promise<boolean>;
    /**
     * Read-only validity check — does NOT flush pending supplier rows or save
     * the schedule of rates. Safe to call on tab entry / page refresh (i.e.
     * whenever the tab is merely being viewed, not left), so the Submit
     * button's `isQuoteTabValid` flag reflects reality without triggering a
     * write.
     */
    validate: () => boolean;
}

export interface QuoteTabProps {
    disabled: boolean;
    inviteStatus: string;
    isClientView: boolean;
    listQuotes: RfxQuoteTable[];
    rfxId?: number;
    rfxInviteId?: number;
    onDraftRevisionChange?: (hasDraft: boolean) => void;
    onQuoteRevisionUpdated?: (isQuoteTabValid?: boolean) => void;
    /** Called after a single quote table's draft is cancelled so the parent can
     * reload the submission and re-hydrate the form with the reverted values. */
    onReloadSubmission?: () => void;
}

const BidderQuoteTab = forwardRef<QuoteTabHandle, QuoteTabProps>(({
    isClientView,
    listQuotes,
    disabled,
    inviteStatus,
    rfxId,
    rfxInviteId,
    onDraftRevisionChange,
    onQuoteRevisionUpdated,
    onReloadSubmission,
}, ref) => {
    const [, { value: values }] = useField<QuoteFieldValues>('quote_answer');
    const { setFieldValue } = useFormikContext();
    const { isLoading: isUnitMeasuresLoading, values: unitsAndMeasures } = useUnitsAndMeasures();

    const supplierRowRefs = useRef<Record<number, SupplierRowsSectionHandle | null>>({});
    const commentRefs = useRef<Record<number, QuoteCommentHandle | null>>({});
    const scheduleRef = useRef<ScheduleOfRatesHandle>(null);
    const localIdCounterRef = useRef(1);
    const [summaryTables, setSummaryTables] = useState<SummaryTableConfig[]>([]);

    const {
        quoteValidationErrors,
        scheduleValidationError,
        clientRowErrors,
        validate,
        clearCellError,
    } = useQuoteValidation(listQuotes, values, isClientView, supplierRowRefs, scheduleRef);

    useImperativeHandle(ref, () => ({
        validateAndSave: async (): Promise<boolean> => {
            // Flush any supplier rows still pending in local state BEFORE we
            // validate or submit. The description-textarea onBlur is what
            // normally creates them; clicking Save races that handler and
            // unsaved rows would otherwise be dropped on refresh.
            const flushes = Object.values(supplierRowRefs.current)
                .filter((h): h is SupplierRowsSectionHandle => !!h)
                .map((h) => h.flushPending());

            // QuoteComment debounces its own save independently of this handle.
            // Flush it in the same await so a comment edited just before a tab
            // switch is persisted before the panel unmounts, instead of being
            // silently dropped by the cancelled debounce timer.
            const commentFlushes = Object.values(commentRefs.current)
                .filter((h): h is QuoteCommentHandle => !!h)
                .map((h) => h.flushPending());

            const [, commentResults] = await Promise.all([
                Promise.all(flushes),
                Promise.all(commentFlushes),
            ]);
            const allCommentsSaved = commentResults.every(Boolean);

            // Deliberately kept separate from `isValid` below: `validate()`
            // failing means the bidder's DATA is invalid (surfaced as red
            // cell highlights / the "your quote is invalid" banner via
            // `isQuoteTabValid`). A comment failing to save is a transient
            // NETWORK failure, not an invalid-data condition — folding it
            // into `isValid` would show that same invalid-data banner over
            // fields that are actually fine. Surface it as its own toast
            // instead, matching how RFQPreview's saveHandler reports its own
            // save failures.
            if (!allCommentsSaved) {
                showToast(
                    'Your comment could not be saved. Please try again.',
                    'Failed To Save',
                    false
                );
            }

            // `validate()` (via `useQuoteValidation`) already calls
            // `scheduleRef.current.validate()` — a pure, local check — and
            // folds its result into `isValid`. So schedule DATA validity is
            // already correct at this point.
            const isValid = validate();

            // Save schedule of rates. This is a network write, not a data
            // check: `save()` can return false either because the DATA is
            // invalid (already captured above) or because the save itself
            // failed (network/API error). Using its return value to gate the
            // function's result would flip `isQuoteTabValid` — and the
            // "please complete all mandatory fields" banner — for fields
            // that are actually fine, exactly the anti-pattern the comment
            // handling above deliberately avoids. Surface a toast instead,
            // matching `allCommentsSaved`.
            if (scheduleRef.current) {
                const scheduleSaved = await scheduleRef.current.save();
                if (!scheduleSaved) {
                    showToast(
                        'Your schedule of rates could not be saved. Please try again.',
                        'Failed To Save',
                        false
                    );
                }
            }

            // `allCommentsSaved` is deliberately NOT folded in here — see the
            // comment above. Its only downstream effect is the toast; it must
            // not flip `isQuoteTabValid` (and with it the "your quote is
            // invalid" banner) for what is actually a save-transport failure.
            // Same reasoning now applies to the schedule save above.
            return isValid;
        },
        validate,
    }), [validate]);

    const handleFormikFieldChange = useCallback((itemId: number, field: string, value: number | string) => {
        setFieldValue(`quote_answer.quote-${itemId}.${field}`, value);
        clearCellError(itemId, field, value);
    }, [setFieldValue, clearCellError]);

    const [showUpdateQuoteModal, setShowUpdateQuoteModal] = useState(false);
    const [updateQuoteData, setUpdateQuoteData] = useState<{
        current: null | QuoteRevisionSnapshot;
        previous: null | QuoteRevisionSnapshot;
        quote: null | RfxQuoteTable;
    }>({ current: null, previous: null, quote: null });
    const [showCompareModal, setShowCompareModal] = useState(false);
    const [compareQuote, setCompareQuote] = useState<RfxQuoteTable | null>(null);
    // Pending per-quote-table cancel confirmation (the quote whose draft is
    // being discarded). Entire-revision cancel lives in the SubmissionToolbar.
    const [cancelTarget, setCancelTarget] = useState<{ quoteId: number } | null>(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const { supplierRowCosts, supplierColTotals, handleSupplierRowsChange } =
        useSupplierRowTotals(listQuotes, rfxInviteId, values);

    const isBeingRevised = inviteStatus === RFX_SUBMISSION_STATUS.BEING_REVISED;
    const autosaveAllowed = isAutosaveAllowed(inviteStatus, isClientView);

    const {
        draftRevisions,
        isQuotesLoading,
        refreshingQuoteId,
        setRefreshingQuoteId,
        quoteDiffKeys,
        fetchDraftRevisions,
        hasDrafts,
    } = useDraftRevisions(rfxInviteId, listQuotes, isBeingRevised, isClientView, disabled);

    useEffect(() => {
        onDraftRevisionChange?.(hasDrafts);
    }, [hasDrafts, onDraftRevisionChange]);

    // Intentionally do NOT auto-validate on initial load. Auto-validating made
    // freshly-reloaded data appear "invalid" with red cell highlights even when
    // the user had just saved successfully — validation rules are stricter than
    // what save accepts (save never blocks on empty cells), so the two
    // disagreed. Validation now runs only when the user clicks Save Progress
    // (via `validateAndSave` on the imperative handle).

    // Fetch summary tables for this RFx
    useEffect(() => {
        if (!rfxId) {
            return;
        }
        getSummaryTables(rfxId).then(setSummaryTables).catch(() => setSummaryTables([]));
    }, [rfxId]);

    // Compute overall totals across ALL quote tables. Each client row is valued
    // from its pending draft-revision cells when a revision is in progress, and
    // from current Formik values otherwise — so the total reflects revised
    // figures + unchanged tables + supplier rows (the effective current total).
    const overallTotals = useMemo(() => {
        let subtotal = 0;

        listQuotes?.forEach((quote) => {
            const clientRows = getClientRows(quote.rows);
            const supplierRows = getSupplierRows(quote.rows, rfxInviteId);
            const draftRevision = draftRevisions[quote.id];
            const useDraft = !!draftRevision && isBeingRevised && !isClientView;
            const draftLookup = useDraft
                ? cellDataToLookup(draftRevision.cells ?? [])
                : null;

            // Column-type lookup: savings are treated as a flat discount while
            // response_value rows multiply by quantity only with one value column.
            const colTypes: Record<number, string> = {};
            quote.columns?.forEach((c) => { colTypes[c.id] = c.columnType; });
            const valueColCount = quote.columns?.filter((c) => c.columnType === 'response_value').length ?? 0;
            const hasQtyCol = quote.columns?.some((c) => c.columnType === 'response_quantity') ?? false;

            // Values a row from its pending draft cells when revised, else Formik.
            const valueRow = (rowId: number): number => {
                const draftCells = draftLookup?.[rowId];
                const rowVals = draftCells
                    ? draftCellsToFormikRow(draftCells, colTypes)
                    : values?.[`quote-${rowId}`] ?? {};
                return computeRowSubtotal(rowVals, colTypes, valueColCount, hasQtyCol);
            };

            clientRows.forEach((row) => { subtotal += valueRow(row.id); });

            if (useDraft) {
                // Revision view: supplier (user-added) rows are revised via the
                // modal — not inline — so the live supplierRowCosts are stale.
                // Value them from their draft cells (or last-submitted Formik).
                supplierRows.forEach((row) => { subtotal += valueRow(row.id); });
            } else {
                // Respond mode: supplier rows are tracked live via supplierRowCosts.
                subtotal += supplierRowCosts[quote.id] ?? 0;
            }
        });

        return {
            subtotal,
            gst: subtotal * GST_RATE,
            totalcost: subtotal + subtotal * GST_RATE,
        };
    }, [listQuotes, draftRevisions, isBeingRevised, isClientView, values, supplierRowCosts, rfxInviteId]);

    const hasAnyTotalColumn = useMemo(() => {
        return listQuotes?.some((q) => q.columns?.some((c) => c.columnType === 'total')) ?? false;
    }, [listQuotes]);

    // Build a map from quote table ID → { columnName → columnId } for response_value columns
    const quoteColumnNameToId = useMemo(() => {
        const result: Record<number, Record<string, number>> = {};
        listQuotes?.forEach((quote) => {
            const map: Record<string, number> = {};
            quote.columns
                ?.filter((c) => c.columnType === 'response_value')
                .forEach((c) => {
                    map[c.name] = c.id;
                });
            result[quote.id] = map;
        });
        return result;
    }, [listQuotes]);

    // Compute live cell values for summary tables from Formik state or draft revision cells
    const summaryLiveCellValues = useMemo(() => {
        if (!summaryTables.length || !listQuotes?.length) {
            return {};
        }
        const result: Record<number, Record<number, Record<string, number>>> = {};

        for (const st of summaryTables) {
            const perTable: Record<number, Record<string, number>> = {};

            for (const qtId of st.quote_table_ids) {
                const quote = listQuotes.find((q) => q.id === qtId);
                if (!quote) {
                    continue;
                }
                const columnMap = quoteColumnNameToId[qtId] ?? {};
                const perColumn: Record<string, number> = {};
                const draft = draftRevisions[qtId];
                const draftCellLookup = draft?.cells?.length
                    ? cellDataToLookup(draft.cells)
                    : null;

                // Collect all row IDs to sum: quote.rows plus any extra rows in the draft
                const knownRowIds = new Set(quote.rows.map((r) => r.id));
                const allRowIds = [...knownRowIds];
                if (draftCellLookup) {
                    for (const rowId of Object.keys(draftCellLookup).map(Number)) {
                        if (!knownRowIds.has(rowId)) {
                            allRowIds.push(rowId);
                        }
                    }
                }

                // Identify supplier row IDs so we can skip them in the Formik/draft loop
                // (their values are tracked separately via supplierColTotals)
                const supplierRowIds = new Set(
                    getSupplierRows(quote.rows, rfxInviteId ?? 0).map((r) => r.id)
                );

                for (const colName of st.value_column_names) {
                    const colId = columnMap[colName];
                    if (colId === undefined) {
                        continue;
                    }
                    // Sum client rows from Formik/draft
                    let sum = 0;
                    for (const rowId of allRowIds) {
                        if (supplierRowIds.has(rowId)) {
                            continue;
                        }
                        let val: number;
                        if (draftCellLookup && draftCellLookup[rowId] !== undefined) {
                            val = Number(draftCellLookup[rowId]?.[colId] ?? 0);
                        } else {
                            const key = `quote-${rowId}`;
                            const rowValues = values?.[key];
                            val = Number(rowValues?.[`rate_${colId}`] ?? 0);
                        }
                        if (!isNaN(val)) {
                            sum += val;
                        }
                    }
                    // Add supplier row totals (managed by SupplierRowsSection)
                    sum += supplierColTotals[qtId]?.[colId] ?? 0;
                    perColumn[colName] = sum;
                }

                perTable[qtId] = perColumn;
            }

            result[st.id] = perTable;
        }

        return result;
    }, [summaryTables, listQuotes, quoteColumnNameToId, values, draftRevisions, supplierColTotals, rfxInviteId]);

    const createRevision = (snapshot: QuoteRevisionSnapshot) => {
        const newRevision = snapshot.revision + 1;
        return { ...snapshot, label: `Revision ${newRevision}`, is_submitted: false, revision: newRevision };
    }

    const handleOpenReviseQuote = useCallback(async (quote: RfxQuoteTable) => {
        if (!rfxInviteId) {
            return;
        }
        try {
            const revisions = await getQuoteRevisions({ inviteId: rfxInviteId, quoteId: quote.id });
            const sorted = [...revisions].sort((a, b) => b.revision - a.revision);

            const isLatestSubmitted = sorted[0].is_submitted;

            // if latest revision has been submitted, we need a new current submission
            const currentRevision = isLatestSubmitted ? createRevision(sorted[0]) : sorted[0];

            // if latest has been submitted, its the previous, otherwise its the one before this
            const previousRevision = isLatestSubmitted ? sorted[0] : sorted[1];

            if (currentRevision && previousRevision) {
                setUpdateQuoteData({ current: currentRevision, previous: previousRevision, quote });
                setShowUpdateQuoteModal(true);
            }
        } catch {
            // Error handled by global interceptor
        }
    }, [rfxInviteId]);

    const handleConfirmCancel = useCallback(async () => {
        if (!rfxInviteId || cancelTarget === null) {
            return;
        }
        setIsCancelling(true);
        try {
            await cancelQuoteRevision({ inviteId: rfxInviteId, quoteId: cancelTarget.quoteId });
            // Reload the submission so Formik re-hydrates from the reverted DB
            // state — the form is seeded with the in-progress (draft) values, so
            // simply dropping the draft locally would leave the revised figures
            // on screen. fetchDraftRevisions is a fallback for parents that do
            // not supply a reload handler.
            if (onReloadSubmission) {
                onReloadSubmission();
            } else {
                setRefreshingQuoteId(cancelTarget.quoteId);
                await fetchDraftRevisions();
            }
            onQuoteRevisionUpdated?.();
            setCancelTarget(null);
        } catch {
            // Error surfaced by the global interceptor; keep the modal open.
        } finally {
            setIsCancelling(false);
        }
    }, [rfxInviteId, cancelTarget, setRefreshingQuoteId, fetchDraftRevisions, onQuoteRevisionUpdated, onReloadSubmission]);

    const unitLookup = useMemo(() => {
        if (!unitsAndMeasures?.length) {
            return undefined;
        }
        return new Map(unitsAndMeasures.map((u) => [u.value, u.label]));
    }, [unitsAndMeasures]);

    const getQuoteComment = (quote: RfxQuoteTable): string => {
        // A locally-saved comment (see quoteCommentsCache above) always wins
        // over the eager-loaded snapshot, which goes stale the moment a save
        // succeeds and isn't refreshed until the next full submission reload.
        if (quoteCommentsCache.has(quote.id)) {
            return quoteCommentsCache.get(quote.id) as string;
        }
        // Comments are eager-loaded and scoped to the current invite by the backend
        return quote.comments?.[0]?.comment ?? "";
    };

    const getQuoteAttachments = (quote: RfxQuoteTable) => {
        if (quoteAttachmentsCache.has(quote.id)) {
            return quoteAttachmentsCache.get(quote.id) as QuoteSupportingDocument[];
        }
        // Attachments are eager-loaded and scoped to the current invite by the backend
        return (quote.attachments ?? []).map((a) => ({
            id: a.id,
            file_name: a.file_name,
            file_path: a.file_path,
            file_size: a.file_size,
            file_type: a.file_type,
        }));
    };

    if (isQuotesLoading) {
        return <LoadingSpinner />
    }

    return (
        <>
            {showUpdateQuoteModal && updateQuoteData.current && updateQuoteData.previous && updateQuoteData.quote && (
                <UpdateQuoteModal
                    isOpen={showUpdateQuoteModal}
                    onClose={() => setShowUpdateQuoteModal(false)}
                    inviteId={rfxInviteId}
                    currentData={updateQuoteData.current}
                    previousData={updateQuoteData.previous}
                    quote={updateQuoteData.quote}
                    onSaveSuccess={(isQuoteTabValid?: boolean) => {
                        onQuoteRevisionUpdated?.(isQuoteTabValid);
                        setRefreshingQuoteId(updateQuoteData.quote?.id ?? null);
                        fetchDraftRevisions();
                    }}
                />
            )}
            {showCompareModal && compareQuote && rfxInviteId && (
                <CompareRevisionsModal
                    isOpen={showCompareModal}
                    onClose={() => setShowCompareModal(false)}
                    columns={compareQuote.columns}
                    inviteId={rfxInviteId}
                    quoteId={compareQuote.id}
                    quoteName={compareQuote.name}
                    quoteItems={compareQuote.rows}
                    initialRevisionA={compareQuote.current_revision}
                    initialRevisionB={compareQuote.current_revision > 0 ? compareQuote.current_revision - 1 : 0}
                />
            )}
            {cancelTarget && (
                <CancelRevisionModal
                    isOpen={cancelTarget !== null}
                    isCancelling={isCancelling}
                    onClose={() => setCancelTarget(null)}
                    onConfirm={handleConfirmCancel}
                />
            )}
            {listQuotes?.map((quote) => {
                const draftRevision = draftRevisions[quote.id];
                const totalRevisions = draftRevision
                    ? draftRevision.revision
                    : quote.current_revision;
                const clientRows = getClientRows(quote.rows);
                const supplierRows = getSupplierRows(quote.rows, rfxInviteId);
                const initialSupplierRows: SupplierRow[] = supplierRowsCache.get(quote.id)
                    ?? getSupplierRows(quote.rows, rfxInviteId).map((r) => mapRowToSupplierRow(r, values, quote.columns, localIdCounterRef));

                return (
                    <RfxQuoteHeader
                        key={`quote-section-${quote.id}`}
                        isBeingRevised={!isClientView && isBeingRevised}
                        hasRevisions={quote.has_revisions}
                        hasValidationError={quoteValidationErrors.has(quote.id)}
                        onOpenReviseQuote={handleOpenReviseQuote}
                        onCompareRevisions={(q) => {
                            setCompareQuote(q);
                            setShowCompareModal(true);
                        }}
                        quote={quote}
                    >
                        {quote.has_revisions && !draftRevision && (
                            <Flex
                                className="mb-3"
                                align="center"
                                gap={2}
                            >
                                <Badge
                                    colorScheme="blue"
                                    data-testid={`revision-badge-${quote.id}`}
                                >
                                    Revision {quote.current_revision} of {totalRevisions}
                                </Badge>
                            </Flex>
                        )}
                        {draftRevision && isBeingRevised && (
                            <Flex
                                className="mb-3"
                                align="center"
                                gap={2}
                            >
                                <Badge
                                    colorScheme="orange"
                                    data-testid={`draft-revision-badge-${quote.id}`}
                                >
                                    Revision {draftRevision.revision}
                                </Badge>
                                {!isClientView && (
                                    <Button
                                        btntype="plain-error"
                                        size="xs"
                                        onClick={() => setCancelTarget({ quoteId: quote.id })}
                                        data-testid={`cancel-revision-${quote.id}`}
                                    >
                                        Cancel revision changes
                                    </Button>
                                )}
                            </Flex>
                        )}
                        {disabled ? (
                            (() => {
                                const cols: QuoteTableColumn[] = (quote.columns ?? []).map((c) => ({
                                    id: c.id,
                                    columnType: c.columnType,
                                    label: c.name,
                                    sortOrder: c.sortOrder,
                                    isDefault: c.isClientEditable,
                                    isSupplierEditable: c.isSupplierEditable,
                                }));

                                // When a draft revision exists, show its values instead of Formik values
                                let allRows;
                                if (draftRevision) {
                                    const cellLookup = cellDataToLookup(draftRevision.cells ?? []);
                                    // Include supplier-added rows alongside client rows — the
                                    // revision snapshot carries supplier-owned cells, so omitting
                                    // them here drops the bidder's own rows from the revised view.
                                    const draftRows = mapCellSnapshotToRows(cellLookup, [...clientRows, ...supplierRows], quote.columns);
                                    allRows = mapQuoteTableRowsToReadOnly(draftRows, cols);
                                } else {
                                    allRows = [
                                        ...buildReadOnlyRows(clientRows, cols, values, unitLookup),
                                        ...buildReadOnlyRows(supplierRows, cols, values, unitLookup, true),
                                    ];
                                }

                                // Σ per-column totals (when procurer enabled includeTotalsRow)
                                let colTotals: Record<number, number> | null = null;
                                if (quote.includeTotalsRow) {
                                    if (draftRevision?.cells?.length) {
                                        // Compute from draft cells; supplier rows without draft cells use supplierColTotals
                                        const draftLookup = cellDataToLookup(draftRevision.cells);
                                        const responseValueCols = cols.filter((c) => c.columnType === 'response_value' || c.columnType === 'saving');
                                        const supplierRowIdSet = new Set(supplierRows.map((r) => r.id));
                                        const totalsMap: Record<number, number> = {};
                                        for (const col of responseValueCols) {
                                            let sum = 0;
                                            let supplierSummedFromDraft = false;
                                            for (const row of [...clientRows, ...supplierRows]) {
                                                if (draftLookup[row.id] !== undefined) {
                                                    const val = Number(draftLookup[row.id]?.[col.id] ?? 0);
                                                    if (!isNaN(val)) {
                                                        sum += val;
                                                    }
                                                    if (supplierRowIdSet.has(row.id)) {
                                                        supplierSummedFromDraft = true;
                                                    }
                                                } else if (!supplierRowIdSet.has(row.id)) {
                                                    // Client row not in draft — read from Formik
                                                    const formikVal = values?.[`quote-${row.id}`]?.[`rate_${col.id}`];
                                                    const val = Number(formikVal ?? 0);
                                                    if (!isNaN(val)) {
                                                        sum += val;
                                                    }
                                                }
                                            }
                                            // If no supplier rows were in the draft, add from supplierColTotals
                                            if (!supplierSummedFromDraft) {
                                                sum += supplierColTotals[quote.id]?.[col.id] ?? 0;
                                            }
                                            totalsMap[col.id] = sum;
                                        }
                                        colTotals = totalsMap;
                                    } else {
                                        // Sum BOTH client and supplier rows straight from Formik
                                        // (supplier rows have their own quote-<id> entries) so the
                                        // column totals always include user-added rows — no reliance
                                        // on the separately-tracked supplierColTotals here.
                                        colTotals = calculateReadOnlyColumnTotals(
                                            cols,
                                            [...clientRows, ...supplierRows],
                                            values
                                        );
                                    }
                                }

                                // Summary rows (only when table has a 'total' column type).
                                // Sum the canonical per-row total across client AND supplier rows,
                                // sourcing each row from its draft cells (when revising) or Formik.
                                const hasTotalColumn = cols.some((c) => c.columnType === 'total');
                                const subtotalDraftLookup = draftRevision
                                    ? cellDataToLookup(draftRevision.cells ?? [])
                                    : null;
                                const subtotalColTypes: Record<number, string> = {};
                                cols.forEach((c) => { subtotalColTypes[c.id] = c.columnType; });
                                const subtotal = hasTotalColumn
                                    ? [...clientRows, ...supplierRows].reduce((acc, r) => {
                                        const draftCells = subtotalDraftLookup?.[r.id];
                                        const rowVals = draftCells
                                            ? draftCellsToFormikRow(draftCells, subtotalColTypes)
                                            : values?.[`quote-${r.id}`] ?? {};
                                        return acc + computeRowTotalFromRowVals(rowVals, cols);
                                    }, 0)
                                    : 0;

                                const isRefreshing = refreshingQuoteId === quote.id;

                                return (
                                    <div className="relative">
                                        {isRefreshing && (
                                            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70">
                                                <Spinner size="lg" color="blue.500" />
                                            </div>
                                        )}
                                        <ReadOnlyQuoteTable
                                            columns={cols}
                                            columnTotals={colTotals}
                                            rows={allRows}
                                            diffKeys={quoteDiffKeys[quote.id]}
                                            totals={hasTotalColumn ? buildReadOnlyTotals(subtotal) : undefined}
                                        />
                                    </div>
                                );
                            })()
                        ) : quote.columns && quote.columns.length > 0 ? (
                            <DynamicQuoteTable
                                columns={quote.columns.map((c) => ({
                                    id: c.id,
                                    columnType: c.columnType,
                                    label: c.name,
                                    sortOrder: c.sortOrder,
                                    isDefault: c.isClientEditable,
                                    isSupplierEditable: c.isSupplierEditable,
                                }))}
                                disabled={disabled}
                                includeTotalsRow={quote.includeTotalsRow === true}
                                items={clientRows}
                                mode={isClientView ? "view" : "respond"}
                                supplierColumnTotals={supplierColTotals[quote.id]}
                                allValues={values}
                                rowErrors={clientRowErrors}
                                onFormikFieldChange={handleFormikFieldChange}
                                supplierRowsRef={rfxInviteId ? (handle: SupplierRowsSectionHandle | null) => { supplierRowRefs.current[quote.id] = handle; } : undefined}
                                supplierRowsProps={rfxInviteId ? {
                                    columns: quote.columns.map((c) => ({
                                        id: c.id,
                                        columnType: c.columnType,
                                        label: c.name,
                                        sortOrder: c.sortOrder,
                                        isDefault: c.isClientEditable,
                                        isSupplierEditable: c.isSupplierEditable,
                                    })),
                                    isAutosaveAllowed: autosaveAllowed,
                                    inviteId: rfxInviteId,
                                    quoteId: quote.id,
                                    startIndex: clientRows.length,
                                    initialRows: initialSupplierRows,
                                    onRowsChange: (rows: SupplierRow[]) => {
                                        supplierRowsCache.set(quote.id, rows);
                                        handleSupplierRowsChange(quote.id, rows);
                                    },
                                    onFormikFieldChange: handleFormikFieldChange,
                                } : undefined}
                                totals={quote.columns?.some((c) => c.columnType === 'total')
                                    ? TOTAL_ROWS.map((row) => ({
                                        id: row.id,
                                        name: row.name,
                                        color: row.color,
                                        value: calculateTotal(
                                            row.id,
                                            clientRows.map((r) => `quote-${r.id}`),
                                            values,
                                            supplierRowCosts[quote.id] ?? 0,
                                            quote.columns,
                                        ),
                                    }))
                                    : []}
                            />
                        ) : (
                            <table className="w-full table-auto rounded-xl border-b border-solid border-[#EAECF0] bg-white">
                                <thead className="h-[44px] bg-[#F9FAFB]">
                                    <tr className="border-b border-[#EAECF0]">
                                        {TABLE_HEADERS.map((th) => (
                                            <th
                                                key={th.name}
                                                className="bg-[#F9FAFB] text-left font-roboto text-[12px] font-medium text-tertiary-600"
                                                style={{ width: th.width }}
                                            >
                                                <div className="ml-5 mt-[3px]">{th.name}</div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {clientRows.map((singleData, itemIndex) => {
                                        const isPercentage = singleData.unitId === 13;
                                        return <tr
                                            key={`quote-item-${singleData.id}`}
                                            className="border-b border-[#EAECF0] hover:bg-blue-200"
                                        >
                                            <td
                                                className="border border-[#EAECF0] px-4 py-2 text-center font-roboto font-normal text-primary-500"
                                                style={{ background: '#F9FAFB', height: '48px' }}
                                            >
                                                {itemIndex + 1}
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] font-roboto"
                                                style={{ color: '#333', backgroundColor: '#fafafa' }}
                                            >
                                                <Field
                                                    name={`quote_answer[quote-${singleData.id}][name]`}
                                                >
                                                    {() => (
                                                        <p className="p-2">{getRowDisplayName(singleData, quote.columns) || '-'}</p>
                                                    )}
                                                </Field>
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] font-roboto"
                                                style={{ color: '#333', backgroundColor: '#fafafa' }}
                                            >
                                                {isUnitMeasuresLoading && (
                                                    <Spinner
                                                        size="sm"
                                                        className="ml-2 mt-1"
                                                    />
                                                )}
                                                {!isUnitMeasuresLoading && (
                                                    <Field
                                                        name={`quote_answer[quote-${singleData.id}][unit]`}
                                                    >
                                                        {({ field }: { field: { value: number } }) => (
                                                            <p className="px-2">
                                                                {
                                                                    unitsAndMeasures?.find(
                                                                        (unit) => unit.value === field?.value
                                                                    )?.label
                                                                }
                                                            </p>
                                                        )}
                                                    </Field>
                                                )}
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] px-4 py-2 font-roboto"
                                                style={{ color: '#333' }}
                                            >
                                                <Field
                                                    name={`quote_answer[quote-${singleData.id}][quantity]`}
                                                >
                                                    {({
                                                        field,
                                                        meta,
                                                        form,
                                                    }: {
                                                        field: React.InputHTMLAttributes<HTMLInputElement> & { name: string };
                                                        form: { setFieldValue: (name: string, value: number | string) => void };
                                                        meta: { error?: string; touched: boolean; };
                                                    }) => (
                                                        <input
                                                            type="number"
                                                            {...field}
                                                            value={handleNumberValue(field.value as string)}
                                                            onChange={(e) =>
                                                                form.setFieldValue(
                                                                    field.name,
                                                                    handleNumberValue(e.target.value)
                                                                )
                                                            }
                                                            className="absolute left-0 top-0 h-full w-full px-5 py-[5px] outline-none"
                                                            style={{
                                                                border:
                                                                    meta.touched && meta.error
                                                                        ? '2px solid #dd0c0cb3'
                                                                        : undefined,
                                                            }}
                                                            placeholder="0"
                                                            min={0}
                                                            onKeyDown={handleKeyDown}
                                                            disabled={disabled}
                                                        />
                                                    )}
                                                </Field>
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] px-4 py-2 font-roboto"
                                                style={{ color: '#333' }}
                                            >
                                                <div className="flex">
                                                    {!isPercentage && <div className="absolute left-2 top-1/2 z-10 -translate-y-1/2">
                                                        $
                                                    </div>}
                                                    <Field
                                                        name={`quote_answer[quote-${singleData.id}][rate]`}
                                                    >
                                                        {({
                                                            field,
                                                            meta,
                                                            form,
                                                        }: {
                                                            field: React.InputHTMLAttributes<HTMLInputElement> & { name: string };
                                                            form: { setFieldValue: (name: string, value: number | string) => void };
                                                            meta: { error?: string; touched: boolean; };
                                                        }) => (
                                                            isPercentage ? <></> :
                                                                <input
                                                                    type="number"
                                                                    {...field}
                                                                    value={handleNumberValue(field.value as string)}
                                                                    onChange={(e) =>
                                                                        form.setFieldValue(
                                                                            field.name,
                                                                            handleNumberValue(e.target.value)
                                                                        )
                                                                    }
                                                                    className="absolute left-0 right-0 top-0 h-full w-full px-5 py-[5px] pl-5 outline-none"
                                                                    style={{
                                                                        border:
                                                                            meta.touched && meta.error
                                                                                ? '2px solid #dd0c0cb3'
                                                                                : undefined,
                                                                    }}
                                                                    placeholder="0"
                                                                    min={0}
                                                                    step={1}
                                                                    onKeyDown={handleKeyDown}
                                                                    disabled={disabled}
                                                                />
                                                        )}
                                                    </Field>
                                                </div>
                                            </td>
                                            <td
                                                className="relative border border-[#EAECF0] px-4 py-2 font-roboto"
                                                style={{ color: '#333' }}
                                            >
                                                <Field
                                                    name={`quote_answer[quote-${singleData.id}][cost]`}
                                                    disabled={true}
                                                >
                                                    {() => (
                                                        isPercentage ? '-' :
                                                            <input
                                                                type="text"
                                                                readOnly
                                                                disabled
                                                                className="absolute left-0 top-0 h-full w-full px-5 py-[5px]"
                                                                value={formatCurrency(
                                                                    (Number(
                                                                        values?.[`quote-${singleData.id}`]
                                                                            ?.quantity ?? 0
                                                                    ) *
                                                                        Number(
                                                                            values?.[`quote-${singleData.id}`]
                                                                                ?.rate ?? 0
                                                                        ))
                                                                )}
                                                            />
                                                    )}
                                                </Field>
                                            </td>
                                        </tr>
                                    })}
                                </tbody>
                                {rfxInviteId && (
                                    <SupplierRowsSection
                                        ref={(handle) => { supplierRowRefs.current[quote.id] = handle; }}
                                        disabled={disabled}
                                        isAutosaveAllowed={autosaveAllowed}
                                        inviteId={rfxInviteId}
                                        isClientView={isClientView}
                                        quoteId={quote.id}
                                        startIndex={clientRows.length}
                                        initialRows={initialSupplierRows}
                                        onRowsChange={(rows) => {
                                            supplierRowsCache.set(quote.id, rows);
                                            handleSupplierRowsChange(quote.id, rows);
                                        }}
                                        onFormikFieldChange={handleFormikFieldChange}
                                    />
                                )}
                                <tbody>
                                    {TOTAL_ROWS.map((list) => (
                                        <tr key={list.id}>
                                            <td
                                                colSpan={isBeingRevised ? 5 : 4}
                                                className="h-[48px] border-none"
                                                style={{ background: '#F9FAFB' }}
                                            />
                                            <td
                                                className="border border-[#EAECF0] bg-gray-utility-100 px-4 py-2"
                                                style={{ color: '#D0D5DD' }}
                                            >
                                                <div
                                                    className="font-roboto font-medium"
                                                    style={{ fontSize: '12px', color: list.color }}
                                                >
                                                    {list.name}
                                                </div>
                                            </td>
                                            <td
                                                className="border border-[#EAECF0] bg-gray-utility-100 px-4 py-2"
                                                style={{ color: '#333' }}
                                            >
                                                {formatCurrency(
                                                    calculateTotal(
                                                        list.id,
                                                        clientRows.map((r) => `quote-${r.id}`),
                                                        values,
                                                        supplierRowCosts[quote.id] ?? 0
                                                    )
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        {rfxInviteId && (
                            <QuoteComment
                                ref={(handle: QuoteCommentHandle | null) => { commentRefs.current[quote.id] = handle; }}
                                initialComment={getQuoteComment(quote)}
                                inviteId={rfxInviteId}
                                isReadOnly={disabled || isClientView}
                                onSaved={(comment) => { quoteCommentsCache.set(quote.id, comment); }}
                                quoteId={quote.id}
                            />
                        )}
                        {rfxInviteId && (
                            <QuoteSupportingDocuments
                                initialDocuments={getQuoteAttachments(quote)}
                                inviteId={rfxInviteId}
                                isReadOnly={disabled || isClientView}
                                onSaved={(documents) => { quoteAttachmentsCache.set(quote.id, documents); }}
                                quoteId={quote.id}
                            />
                        )}
                    </RfxQuoteHeader>
                );
            })}
            {summaryTables.length > 0 && rfxId && (
                <div className="mt-6 space-y-6">
                    {summaryTables.map((st) => (
                        <SummaryTable
                            key={st.id}
                            categoryNames={st.value_column_names}
                            inviteId={rfxInviteId}
                            liveCellValues={summaryLiveCellValues[st.id]}
                            mode={isClientView ? "view" : "respond"}
                            quoteTableIds={st.quote_table_ids}
                            quoteTableNames={listQuotes?.reduce((acc, q) => {
                                acc[q.id] = q.name;
                                return acc;
                            }, {} as Record<number, string>) ?? {}}
                            rfxId={rfxId}
                            summaryTableId={st.id}
                            summaryTableName={st.name}
                        />
                    ))}
                </div>
            )}
            {hasAnyTotalColumn && (
                <OverallTotalsSummary
                    subtotal={overallTotals.subtotal}
                    gst={overallTotals.gst}
                    totalcost={overallTotals.totalcost}
                />
            )}
            {rfxInviteId && (
                <ScheduleOfRates
                    ref={scheduleRef}
                    inviteId={rfxInviteId}
                    disabled={disabled}
                    hasValidationError={scheduleValidationError}
                    isClientView={isClientView}
                />
            )}
        </>
    );
});

BidderQuoteTab.displayName = "QuoteTab";

export { BidderQuoteTab as QuoteTab };
